import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material';
import {
  LocalFireDepartmentRounded,
  AccessTimeRounded,
  EventNoteRounded,
  EmojiEventsRounded,
  PlaceRounded,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import PageShell from '../../components/shared/PageShell';
import SectionHeader from '../../components/shared/SectionHeader';
import StatCard from '../../components/shared/StatCard';
import EmptyState from '../../components/shared/EmptyState';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';
import { RADIUS } from '../../theme/tokens';

// StreakFlame — inline component (<30 lines)
function StreakFlame({ streak }) {
  const theme = useTheme();
  if (!streak) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <LocalFireDepartmentRounded sx={{ fontSize: 18, color: theme.palette.warning.main }} />
      <Typography variant="caption" fontWeight={700} color="warning.main">
        {streak} day streak
      </Typography>
    </Box>
  );
}

// Custom Recharts tooltip
function ChartTooltip({ active, payload, label }) {
  const theme = useTheme();
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        bgcolor: 'background.elevated',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: RADIUS.md,
        px: 1.5,
        py: 1,
        boxShadow: theme.shadowsExt?.sm,
      }}
    >
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={700} color="primary.main">
        {payload[0].value}h
      </Typography>
    </Box>
  );
}

function StudentStatistics() {
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchStatistics();
  }, [isAuthenticated, navigate, fetchStatistics]);

  const fetchStatistics = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/statistics?period=${period}`);
      setStats(res.data.data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  const hasData = stats && (stats.summary?.total_hours > 0 || stats.summary?.total_sessions > 0);

  // Normalize library visits for progress bars
  const topLibraries = stats?.patterns?.top_libraries ?? [];
  const maxSessions = topLibraries.length > 0 ? topLibraries[0].sessions : 1;

  // Chart data — map chart_data array
  const chartData = (stats?.chart_data ?? []).map((d) => ({
    name: d.day,
    hours: d.hours ?? 0,
  }));

  // Derived period label for a11y
  const periodLabel = period === '7' ? '7 days' : period === '30' ? '30 days' : '90 days';

  if (loading) {
    return (
      <PageShell title="Study statistics" backTo={-1}>
        <LoadingSkeleton variant="hero" sx={{ height: 52, mb: 2 }} />
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={6} key={i}>
              <LoadingSkeleton variant="stat" />
            </Grid>
          ))}
        </Grid>
        <LoadingSkeleton variant="hero" sx={{ height: 180, mb: 2 }} />
        <LoadingSkeleton variant="list" count={3} />
      </PageShell>
    );
  }

  return (
    <PageShell title="Study statistics" backTo={-1}>
      <Box>
          {/* Period toggle — restyled via theme, no inline gradients */}
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={(_, val) => val && setPeriod(val)}
            fullWidth
            aria-label="Select time period"
            sx={{
              mb: 3,
              bgcolor: 'background.paper',
              borderRadius: RADIUS.lg,
              p: 0.5,
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 600,
                border: 'none',
                borderRadius: `${RADIUS.md}px !important`,
                color: 'text.secondary',
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' },
                },
              },
            }}
          >
            <ToggleButton value="7">Week</ToggleButton>
            <ToggleButton value="30">Month</ToggleButton>
            <ToggleButton value="90">Year</ToggleButton>
          </ToggleButtonGroup>

          {/* Streak badge */}
          {stats?.summary?.current_streak > 0 && (
            <Box sx={{ mb: 2 }}>
              <StreakFlame streak={stats.summary.current_streak} />
            </Box>
          )}

          {/* Stat grid 2x2 */}
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <StatCard
                icon={<AccessTimeRounded />}
                label="Total hours"
                value={stats?.summary?.total_hours ?? 0}
                tone="primary"
              />
            </Grid>
            <Grid item xs={6}>
              <StatCard
                icon={<EventNoteRounded />}
                label="Days present"
                value={stats?.summary?.days_present ?? stats?.summary?.total_sessions ?? 0}
                tone="accent"
              />
            </Grid>
            <Grid item xs={6}>
              <StatCard
                icon={<LocalFireDepartmentRounded />}
                label="Current streak"
                value={stats?.summary?.current_streak ?? 0}
                tone="success"
              />
            </Grid>
            <Grid item xs={6}>
              <StatCard
                icon={<EmojiEventsRounded />}
                label="Best streak"
                value={stats?.summary?.best_streak ?? stats?.summary?.longest_streak ?? 0}
                tone="coin"
              />
            </Grid>
          </Grid>

          {/* Area chart */}
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: RADIUS.lg,
              boxShadow: theme.shadowsExt?.sm,
              p: 2.5,
              mb: 3,
            }}
          >
            <SectionHeader title={`Hours per day (${periodLabel})`} />

            {!hasData || chartData.length === 0 ? (
              <Box sx={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <EmptyState
                  icon={<AccessTimeRounded />}
                  title="Check in to start tracking"
                  description="Your study hours will appear here once you start sessions."
                  actionLabel="Browse libraries"
                  onAction={() => navigate('/student/libraries')}
                  sx={{ py: 2 }}
                />
              </Box>
            ) : (
              <Box
                aria-label={`Hours studied per day over the last ${periodLabel}. Total: ${stats?.summary?.total_hours ?? 0} hours.`}
                role="img"
              >
                <ResponsiveContainer width="100%" height={160}>
                  <AreaChart data={chartData} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                    <defs>
                      <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.28} />
                        <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="hours"
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                      fill="url(#chartGrad)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>

          {/* Most visited libraries */}
          {topLibraries.length > 0 && (
            <Box>
              <SectionHeader title="Most visited libraries" icon={<PlaceRounded sx={{ fontSize: 20 }} />} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {topLibraries.slice(0, 5).map((lib) => (
                  <Box key={lib.library_id ?? lib.name}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, mr: 1 }}>
                        {lib.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {lib.sessions} session{lib.sessions !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={maxSessions > 0 ? (lib.sessions / maxSessions) * 100 : 0}
                      sx={{
                        height: 6,
                        borderRadius: RADIUS.pill,
                        bgcolor: isLight ? 'rgba(242,101,34,0.12)' : 'rgba(255,138,80,0.12)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: 'primary.main',
                          borderRadius: RADIUS.pill,
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Fallback when no data at all */}
          {!hasData && (
            <EmptyState
              icon={<EventNoteRounded />}
              title="Check in to start tracking"
              description="Your study statistics will show up here after your first session."
              actionLabel="Browse libraries"
              onAction={() => navigate('/student/libraries')}
            />
          )}
      </Box>
    </PageShell>
  );
}

export default StudentStatistics;
